import {
  AppliedFilters,
  Container,
  Table,
  useFilters,
} from "@fluidattacks/design";
import type { Row } from "@tanstack/react-table";
import { sortBy } from "lodash";
import { useCallback, useContext } from "react";
import { useParams } from "react-router-dom";

import { FindingHighlightedFilters } from "./highlighted-filters";
import { useFindingFilters } from "./hooks/use-finding-filters";
import {
  updateFindingFilterStore,
  useGetFindingFiltersStore,
} from "./hooks/use-finding-filters-store";

import { renderDescription } from "../description";
import { ExtraButtons } from "../extra-buttons";
import type { IFindingsQuery } from "../hooks";
import type { IFindingAttr } from "../types";
import { getTableColumns } from "../utils";
import { CVSS3Toggle } from "components/cvss3-toggle";
import { meetingModeContext } from "context/meeting-mode";
import { ExpertButton } from "features/expert-button";
import type { FindingFiltersInput, GetFindingsQueryQuery } from "gql/graphql";
import { useAuthz } from "hooks/use-authz";
import { useTable } from "hooks/use-table";
import { translate } from "utils/translations/translate";
import type { TStore } from "utils/zustand";

interface IFindingTableProps {
  group: GetFindingsQueryQuery["group"] | undefined;
  findings: IFindingAttr[];
  refetch: IFindingsQuery["refetch"];
  loading: boolean;
}

const FindingsTable = (props: IFindingTableProps): JSX.Element => {
  const { group, findings, loading } = props;

  // Parent hooks
  const { groupName } = useParams() as { groupName: string };
  const { canMutate, canResolve } = useAuthz();
  const canGetHacker = canResolve("finding_hacker");
  const canRemoveFinding = canMutate("remove_finding");
  const { meetingMode } = useContext(meetingModeContext);

  const columns = getTableColumns(
    canGetHacker,
    group?.totalOpenPriority ?? 0,
    meetingMode,
  );
  const { search } = useGetFindingFiltersStore(groupName);

  const { filterOptions, filtersInfo, onFiltersChange, getValues, setValues } =
    useFindingFilters({
      groupName,
      loading,
    });

  const handleRowExpand = useCallback((row: Row<IFindingAttr>): JSX.Element => {
    return renderDescription(row.original);
  }, []);

  const { Filters, removeFilter, options } = useFilters({
    getValues,
    onFiltersChange,
    options: filterOptions,
    setValues,
    variant: "grouped",
  });

  const setSearch = useCallback(
    (searchValue: string): void => {
      updateFindingFilterStore(groupName, { search: searchValue });
    },
    [groupName],
  );

  const filteredFindings = findings.filter((finding): boolean =>
    meetingMode ? finding.status !== "DRAFT" : true,
  );

  const { table } = useTable({
    columns,
    data: filteredFindings,
    filters: {} as TStore<FindingFiltersInput, "">,
    id: "findings-table",
    options: {
      actionsOptions: {
        onSearch: setSearch,
        search,
        searchPlaceholder: "Search by weakness",
      },
      columnOptions: {
        hidden: [
          "age",
          "Treatment",
          "reattack",
          "releaseDate",
          "closingPercentage",
        ],
        order: [
          "title",
          "status",
          "priority",
          "openVulns",
          "severityOverview",
          "lastVulnerability",
          "hacker",
        ],
        pinned: ["title", "status", "priority"],
        sorting: [{ desc: true, id: "priority" }],
      },
      filtersMode: "client-side",
      loading,
      paginationMode: "client-side",
      rowOptions: {
        enableMultiSelection: canRemoveFinding,
        expandRender: handleRowExpand,
      },
    },
    total: filteredFindings.length,
  });

  const { rowSelection } = table.getState();
  const { data } = table.options.meta ?? {};
  const selectedFindings = Object.keys(rowSelection).map(
    (key): IFindingAttr => data?.get(key) ?? ({} as IFindingAttr),
  );

  return (
    <Table table={table}>
      <Table.Options>
        <Table.Highlighted>
          <FindingHighlightedFilters groupName={groupName} />
        </Table.Highlighted>
        <Table.Controls>
          <Filters />
          <Table.Columns table={table} />
          <Container gap={0.5} id={"actions-container"}>
            <CVSS3Toggle />
            <ExtraButtons
              group={group}
              loadingFindings={loading}
              selectedFindings={selectedFindings}
              typesOptions={sortBy(
                filtersInfo.findingTitles,
                (title): string => title,
              )}
            />
          </Container>
        </Table.Controls>
      </Table.Options>
      <Table.Caption>
        <AppliedFilters onClose={removeFilter} options={options} />
      </Table.Caption>
      <Table.Content />
      <Table.Footer>
        <Table.FooterContent>
          <ExpertButton
            display={"flex"}
            featurePreview={true}
            text={translate.t("searchFindings.tabDescription.helpButton")}
          />
        </Table.FooterContent>
      </Table.Footer>
    </Table>
  );
};

export { FindingsTable };
